<!-- Loading State -->
<div *ngIf="isLoading" class="d-flex justify-content-center align-items-center min-h-400px">
  <div class="spinner-border text-primary" role="status">
    <span class="visually-hidden">Loading...</span>
  </div>
</div>

<!-- View Mode - Ticket Detail -->
<ng-container *ngIf="!isLoading && isViewMode && ticket">
  <!-- Real-time Update Notification -->
  <div *ngIf="hasRealtimeUpdate" class="alert alert-info alert-dismissible fade show mb-3" role="alert">
    <i class="bi bi-lightning-charge me-2"></i>
    <strong>Live Update:</strong> This ticket has been updated. The changes are shown below.
    <button type="button" class="btn-close" (click)="dismissUpdateNotification()" aria-label="Close"></button>
  </div>

  <!-- Connection Status Indicator (subtle) -->
  <div class="d-flex justify-content-end mb-2">
    <small class="d-flex align-items-center gap-1" [ngClass]="{
      'text-success': connectionState === 'authenticated',
      'text-warning': connectionState === 'connecting' || connectionState === 'connected',
      'text-muted': connectionState === 'disconnected',
      'text-danger': connectionState === 'error'
    }">
      <span class="status-dot" [ngClass]="{
        'status-dot-connected': connectionState === 'authenticated',
        'status-dot-connecting': connectionState === 'connecting' || connectionState === 'connected',
        'status-dot-disconnected': connectionState === 'disconnected' || connectionState === 'error'
      }"></span>
      <span *ngIf="connectionState === 'authenticated'">Live updates active</span>
      <span *ngIf="connectionState === 'connecting' || connectionState === 'connected'">Connecting...</span>
      <span *ngIf="connectionState === 'disconnected'">Offline</span>
      <span *ngIf="connectionState === 'error'">Connection error</span>
    </small>
  </div>

  <!-- Breadcrumb and Header -->
  <div class="d-flex justify-content-between align-items-start mb-4">
    <div>
      <nav aria-label="breadcrumb" class="mb-2">
        <ol class="breadcrumb mb-0">
          <li class="breadcrumb-item">
            <a routerLink="/maintenance/support-tickets" class="text-decoration-none">Support Tickets</a>
          </li>
          <li class="breadcrumb-item active" aria-current="page">{{ ticket.ticketNumber }}</li>
        </ol>
      </nav>
      <h1 class="h2 mb-1 fw-bold">{{ ticket.subject }}</h1>
      <div class="d-flex gap-2 align-items-center">
        <span class="badge" [ngClass]="getStatusBadgeClass(ticket.status)">
          {{ getStatusText(ticket.status) }}
        </span>
        <span class="badge" [ngClass]="getPriorityBadgeClass(ticket.priority)">
          {{ getPriorityText(ticket.priority) }} Priority
        </span>
        <span class="text-muted">{{ ticket.ticketNumber }}</span>
      </div>
    </div>
    <div class="d-flex gap-2" *ngIf="ticket.status === 'open' || ticket.status === 'in_progress'">
      <button
        type="button"
        class="btn btn-success"
        (click)="resolveTicket()"
        [disabled]="isUpdatingStatus"
      >
        <span *ngIf="isUpdatingStatus" class="spinner-border spinner-border-sm me-2"></span>
        <i *ngIf="!isUpdatingStatus" class="bi bi-check-circle me-2"></i>Mark Resolved
      </button>
      <button
        type="button"
        class="btn btn-outline-warning"
        (click)="withdrawTicket()"
        [disabled]="isUpdatingStatus"
        *ngIf="ticket.status === 'open'"
      >
        <i class="bi bi-x-circle me-2"></i>Withdraw Ticket
      </button>
    </div>
    <div class="d-flex gap-2" *ngIf="ticket.status === 'resolved'">
      <button
        type="button"
        class="btn btn-outline-primary"
        (click)="reopenTicket()"
        [disabled]="isUpdatingStatus"
      >
        <span *ngIf="isUpdatingStatus" class="spinner-border spinner-border-sm me-2"></span>
        <i *ngIf="!isUpdatingStatus" class="bi bi-arrow-counterclockwise me-2"></i>Reopen Ticket
      </button>
    </div>
  </div>

  <div class="row">
    <!-- Main Content -->
    <div class="col-lg-8">
      <!-- Original Description -->
      <div class="card mb-4">
        <div class="card-header">
          <h5 class="mb-0">Description</h5>
        </div>
        <div class="card-body">
          <div class="ticket-description mb-0" [innerHTML]="ticket.description"></div>
        </div>
      </div>

      <!-- Resolution (shown when ticket is resolved/closed and has resolution text) -->
      <div class="card mb-4" *ngIf="ticket.resolution && (ticket.status === 'resolved' || ticket.status === 'closed')">
        <div class="card-header bg-success-subtle">
          <h5 class="mb-0 text-success"><i class="bi bi-check-circle me-2"></i>Resolution</h5>
        </div>
        <div class="card-body">
          <div class="ticket-resolution mb-0" [innerHTML]="ticket.resolution"></div>
        </div>
      </div>

      <!-- Activity Timeline -->
      <div class="card mb-4">
        <div class="card-header">
          <h5 class="mb-0">Activity</h5>
        </div>
        <div class="card-body p-0">
          <!-- Show older messages button -->
          <div *ngIf="hiddenMessageCount > 0 && !showOlderMessages" class="text-center py-3 border-bottom">
            <button type="button" class="btn btn-link text-decoration-none" (click)="toggleOlderMessages()">
              <i class="bi bi-clock-history me-1"></i>
              Show {{ hiddenMessageCount }} older message{{ hiddenMessageCount > 1 ? 's' : '' }}
            </button>
          </div>

          <div class="position-relative p-3">
            <div *ngFor="let item of visibleActivity; let last = last"
                 class="timeline-item"
                 [class.timeline-item-last]="last">
              <!-- System/Status Updates -->
              <ng-container *ngIf="item.type === 'system' || item.type === 'status_change'">
                <div class="timeline-marker timeline-marker-system">
                  <i class="bi" [ngClass]="getActivityIcon(item)"></i>
                </div>
                <div class="timeline-content timeline-content-system">
                  <span class="text-muted">{{ item.content }}</span>
                  <span class="text-muted small ms-2">{{ formatRelativeTime(item.createdAt) }}</span>
                </div>
              </ng-container>

              <!-- Thread / Reply Items -->
              <ng-container *ngIf="item.type === 'thread'">
                <div class="timeline-marker" [ngClass]="{
                  'timeline-marker-agent': item.authorType === 'agent' || item.authorType === 'contact',
                  'timeline-marker-user': item.authorType === 'user'
                }">
                  <i class="bi bi-person-fill"></i>
                </div>
                <div class="timeline-content">
                  <div class="card">
                    <div class="card-header py-2">
                      <div class="d-flex justify-content-between align-items-center">
                        <div class="d-flex align-items-center gap-2">
                          <span class="fw-medium">{{ item.author }}</span>
                          <span *ngIf="item.authorType === 'user'" class="badge small bg-success-subtle text-success">
                            You
                          </span>
                          <span *ngIf="item.authorType === 'agent' || item.authorType === 'contact'" class="small text-primary">
                            Support
                          </span>
                          <span *ngIf="item.direction === 'inbound'" class="badge bg-info-subtle text-info small">
                            <i class="bi bi-arrow-down-left me-1"></i>Inbound
                          </span>
                        </div>
                        <span class="text-muted small">{{ formatRelativeTime(item.createdAt) }}</span>
                      </div>
                    </div>
                    <div class="card-body py-3">
                      <div class="mb-0 text-pre-wrap" [innerHTML]="item.content"></div>
                    </div>
                  </div>
                </div>
              </ng-container>
            </div>
          </div>

          <!-- Hide older messages button (when expanded) -->
          <div *ngIf="hiddenMessageCount > 0 && showOlderMessages" class="text-center py-3 border-top">
            <button type="button" class="btn btn-link text-decoration-none" (click)="toggleOlderMessages()">
              <i class="bi bi-chevron-up me-1"></i>
              Hide older messages
            </button>
          </div>
        </div>
      </div>

      <!-- Add Reply -->
      <div class="card" *ngIf="ticket.status !== 'closed'">
        <div class="card-header">
          <h5 class="mb-0">Add Reply</h5>
        </div>
        <div class="card-body">
          <textarea appCharCounter
            class="form-control mb-3"
            rows="4"
            [(ngModel)]="newComment"
            placeholder="Type your reply here..."
            [disabled]="isAddingComment"
          ></textarea>
          <div class="d-flex justify-content-end">
            <button
              type="button"
              class="btn btn-primary"
              (click)="addComment()"
              [disabled]="!newComment.trim() || isAddingComment"
            >
              <span *ngIf="isAddingComment" class="spinner-border spinner-border-sm me-2"></span>
              <i *ngIf="!isAddingComment" class="bi bi-send me-2"></i>
              Send Reply
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Sidebar - Ticket Details -->
    <div class="col-lg-4">
      <div class="sticky-top sticky-sidebar">
      <div class="card">
        <div class="card-header">
          <h5 class="mb-0">Details</h5>
        </div>
        <div class="card-body">
          <dl class="row mb-0">
            <dt class="col-sm-5 text-muted">Status</dt>
            <dd class="col-sm-7">
              <span class="badge" [ngClass]="getStatusBadgeClass(ticket.status)">
                {{ getStatusText(ticket.status) }}
              </span>
            </dd>

            <dt class="col-sm-5 text-muted">Priority</dt>
            <dd class="col-sm-7">
              <span class="badge" [ngClass]="getPriorityBadgeClass(ticket.priority)">
                {{ getPriorityText(ticket.priority) }}
              </span>
            </dd>

            <dt class="col-sm-5 text-muted">Category</dt>
            <dd class="col-sm-7">{{ ticket.category }}</dd>

            <dt class="col-sm-5 text-muted" *ngIf="ticket.droneName">Drone</dt>
            <dd class="col-sm-7" *ngIf="ticket.droneName">
              <div>{{ ticket.droneName }}</div>
              <div class="text-muted small">{{ ticket.droneSerialNumber }}</div>
            </dd>

            <dt class="col-sm-5 text-muted">Created</dt>
            <dd class="col-sm-7">{{ formatDateTime(ticket.createdAt) }}</dd>

            <dt class="col-sm-5 text-muted">Last Updated</dt>
            <dd class="col-sm-7">{{ formatDateTime(ticket.updatedAt) }}</dd>

            <dt class="col-sm-5 text-muted">Created By</dt>
            <dd class="col-sm-7">{{ ticket.createdByName || 'Unknown' }}</dd>

            <dt class="col-sm-5 text-muted" *ngIf="ticket.assignedToName">Assigned To</dt>
            <dd class="col-sm-7" *ngIf="ticket.assignedToName">{{ ticket.assignedToName }}</dd>
          </dl>
        </div>
      </div>

      <!-- Attachments -->
      <div class="card mt-3" *ngIf="ticket.attachments.length > 0">
        <div class="card-header d-flex justify-content-between align-items-center">
          <h5 class="mb-0"><i class="bi bi-paperclip me-2"></i>Attachments</h5>
          <span class="badge bg-secondary">{{ ticket.attachments.length }}</span>
        </div>
        <div class="card-body p-0">
          <div class="list-group list-group-flush">
            <a *ngFor="let attachment of ticket.attachments"
               [href]="getAttachmentUrl(attachment)"
               target="_blank"
               class="list-group-item list-group-item-action d-flex align-items-center gap-2"
               [class.disabled]="!attachment.downloadUrl"
               (click)="onAttachmentClick($event, attachment)">
              <i class="bi fs-5" [ngClass]="getAttachmentIcon(attachment)"></i>
              <div class="flex-grow-1 overflow-hidden">
                <div class="text-truncate small fw-medium">{{ attachment.fileName }}</div>
                <div class="text-muted small">{{ formatAttachmentSize(attachment) }}</div>
              </div>
              <i class="bi text-muted" [ngClass]="attachment.isImage || attachment.isPdf ? 'bi-box-arrow-up-right' : 'bi-download'"></i>
            </a>
          </div>
        </div>
      </div>
      </div>
    </div>
  </div>
</ng-container>

<!-- Create Mode - Ticket Form -->
<ng-container *ngIf="!isLoading && !isViewMode">
  <!-- Header -->
  <div class="d-flex justify-content-between align-items-start mb-4">
    <div>
      <nav aria-label="breadcrumb" class="mb-2">
        <ol class="breadcrumb mb-0">
          <li class="breadcrumb-item">
            <a routerLink="/maintenance/support-tickets" class="text-decoration-none">Support Tickets</a>
          </li>
          <li class="breadcrumb-item active" aria-current="page">New Ticket</li>
        </ol>
      </nav>
      <h1 class="h2 mb-1 fw-bold">Create Support Ticket</h1>
      <p class="text-muted mb-0">Submit a new support request to our team</p>
    </div>
  </div>

  <div class="row">
    <div class="col-lg-8">
      <form [formGroup]="form" (ngSubmit)="onSubmit()">
        <div class="card mb-4">
          <div class="card-header">
            <h5 class="mb-0">Ticket Information</h5>
          </div>
          <div class="card-body">
            <!-- Subject -->
            <div class="mb-3">
              <label for="subject" class="form-label">Subject <span class="text-danger">*</span></label>
              <input
                type="text"
                id="subject"
                class="form-control"
                formControlName="subject"
                placeholder="Brief summary of your issue"
                maxlength="200"
                [class.is-invalid]="isFieldInvalid('subject')"
              >
              <div class="invalid-feedback" *ngIf="isFieldInvalid('subject')">
                {{ getFieldError('subject') }}
              </div>
            </div>

            <!-- Category -->
            <div class="mb-3">
              <label for="category" class="form-label">Category <span class="text-danger">*</span></label>
              <select
                id="category"
                class="form-select"
                formControlName="category"
                [class.is-invalid]="isFieldInvalid('category')"
              >
                <option value="">Select a category</option>
                <option *ngFor="let cat of categories" [value]="cat">{{ cat }}</option>
              </select>
              <div class="invalid-feedback" *ngIf="isFieldInvalid('category')">
                {{ getFieldError('category') }}
              </div>
            </div>

            <!-- Related Drone -->
            <div class="mb-3">
              <label for="droneId" class="form-label">Related Drone (Optional)</label>
              <select id="droneId" class="form-select" formControlName="droneId">
                <option value="">No specific drone</option>
                <option *ngFor="let drone of drones" [value]="drone.id">
                  {{ drone.name }} ({{ drone.serialNumber }})
                </option>
              </select>
              <div class="form-text">If your issue is related to a specific drone, select it here.</div>
            </div>

            <!-- Description -->
            <div class="mb-3">
              <label for="description" class="form-label">Description <span class="text-danger">*</span></label>
              <textarea appCharCounter
                id="description"
                class="form-control"
                formControlName="description"
                rows="6"
                placeholder="Please describe your issue in detail. Include any error messages, steps to reproduce, and what you've already tried."
                [class.is-invalid]="isFieldInvalid('description')"
              ></textarea>
              <div class="invalid-feedback" *ngIf="isFieldInvalid('description')">
                {{ getFieldError('description') }}
              </div>
              <div class="form-text">Minimum 20 characters</div>
            </div>
          </div>
        </div>

        <!-- Attachments -->
        <div class="card mb-4">
          <div class="card-header">
            <h5 class="mb-0">Attachments</h5>
          </div>
          <div class="card-body">
            <!-- Drop Zone -->
            <div
              class="drop-zone p-4 border border-2 border-dashed rounded text-center mb-3"
              (dragover)="onDragOver($event)"
              (drop)="onDrop($event)"
              [class.drop-zone-active]="false"
            >
              <i class="bi bi-cloud-arrow-up fs-1 text-muted"></i>
              <p class="mb-2">Drag and drop files here, or</p>
              <label class="btn btn-outline-primary">
                <i class="bi bi-folder2-open me-2"></i>Browse Files
                <input
                  type="file"
                  class="d-none"
                  multiple
                  accept="image/*,application/pdf,video/mp4,video/quicktime"
                  (change)="onFileSelect($event)"
                >
              </label>
              <p class="text-muted small mt-2 mb-0">
                Supported: Images, PDF, Videos (Max 25MB each, up to 5 files)
              </p>
            </div>

            <!-- Pending Attachments List -->
            <div *ngIf="pendingAttachments.length > 0">
              <h6 class="text-muted mb-2">Files to upload ({{ pendingAttachments.length }})</h6>
              <div class="list-group">
                <div
                  *ngFor="let file of pendingAttachments; let i = index"
                  class="list-group-item d-flex align-items-center"
                >
                  <i class="bi me-3 fs-4" [ngClass]="getFileIcon(file.type)"></i>
                  <div class="flex-grow-1">
                    <div class="fw-medium">{{ file.name }}</div>
                    <div class="text-muted small">{{ formatFileSize(file.size) }}</div>
                  </div>
                  <button
                    type="button"
                    class="btn btn-sm btn-outline-danger"
                    (click)="removeAttachment(i)"
                  >
                    <i class="bi bi-x-lg"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Form Actions -->
        <div class="d-flex gap-3 justify-content-end">
          <button type="button" class="btn btn-outline-secondary" (click)="cancel()">
            Cancel
          </button>
          <button
            type="submit"
            class="btn btn-primary"
            [disabled]="form.invalid || isSaving"
          >
            <span *ngIf="isSaving" class="spinner-border spinner-border-sm me-2"></span>
            <i *ngIf="!isSaving" class="bi bi-check-lg me-2"></i>
            Create Ticket
          </button>
        </div>
      </form>
    </div>

    <!-- Sidebar - Tips -->
    <div class="col-lg-4">
      <div class="sticky-top sticky-sidebar">
      <div class="card">
        <div class="card-header">
          <h5 class="mb-0"><i class="bi bi-lightbulb me-2"></i>Tips for Faster Resolution</h5>
        </div>
        <div class="card-body">
          <ul class="list-unstyled mb-0">
            <li class="mb-3">
              <div class="d-flex gap-2">
                <i class="bi bi-check-circle text-success"></i>
                <div>
                  <strong>Be specific</strong>
                  <p class="text-muted small mb-0">Include exact error messages and what you were doing when the issue occurred.</p>
                </div>
              </div>
            </li>
            <li class="mb-3">
              <div class="d-flex gap-2">
                <i class="bi bi-check-circle text-success"></i>
                <div>
                  <strong>Add screenshots</strong>
                  <p class="text-muted small mb-0">Visual evidence helps us understand and diagnose issues faster.</p>
                </div>
              </div>
            </li>
            <li class="mb-3">
              <div class="d-flex gap-2">
                <i class="bi bi-check-circle text-success"></i>
                <div>
                  <strong>Select the right drone</strong>
                  <p class="text-muted small mb-0">If the issue is equipment-related, selecting the specific drone helps us check your records.</p>
                </div>
              </div>
            </li>
          </ul>
        </div>
      </div>

      <!-- Technical Support Phone Card -->
      <div class="card mt-3">
        <div class="card-body">
          <div class="d-flex align-items-center gap-3">
            <div class="rounded-circle bg-primary-subtle p-3">
              <i class="bi bi-telephone text-primary fs-4"></i>
            </div>
            <div>
              <div class="text-muted small">Technical Support Hotline</div>
              <a [href]="'tel:' + technicalSupportPhone" class="fs-5 fw-semibold text-decoration-none">
                {{ formattedSupportPhone }}
              </a>
            </div>
          </div>
        </div>
      </div>
      </div>
    </div>
  </div>

  <!-- Withdraw Ticket Modal -->
  <app-withdraw-ticket-modal
    #withdrawModal
    (confirmed)="onWithdrawConfirmed($event)"
    (cancelled)="onWithdrawCancelled()"
  ></app-withdraw-ticket-modal>
</ng-container>
